import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Popover, Box, Button, IconButton } from '@mui/material';
import moment from 'moment';

import { ITherapy } from 'interfaces/redux/ITherapy';
import { IState } from 'interfaces/redux/IState';
import { ControlledDatePickerCalendar } from 'components/react-hook-form-fields/date-picker/controlled-date-picker';
import ConfirmationPanel from 'components/form/confirmation/confirmation-panel';
import { CalendarToday } from '@mui/icons-material';
import { editTherapiesNBD } from 'actions/action-therapies';
import { ControlledDropdown } from 'components/react-hook-form-fields';

import { buildQaIdProp, buildQaId } from 'utils/build-qa-id';
import { nameOfFactory } from 'utils/types-util';

import { convertToArborDate } from 'models/time/arbor-date';
import { getTherapyFillCycleReasonOptions } from 'services/utils/task-service';
import { clearSelectedTasks } from 'actions/action-tasks';

type TherapyType = { patient_id: number; id: number };

interface TherapyDateFormProps {
  therapy: TherapyType | ITherapy | TherapyType[];
  disabled?: boolean;
  title?: string;
  iconButtonType?: boolean;
  initialDate?: moment.Moment | null;
}

interface IFormFields {
  needsby_date?: Date;
  reason?: string;
}

const qaIdProp = buildQaIdProp('nbd-therapy-form');
const qaId = buildQaId('nbd-therapy-form');
const getFieldName = nameOfFactory<IFormFields>();
export const NeedsByDateDialogForm = React.forwardRef(
  ({ therapy, iconButtonType, ...props }: TherapyDateFormProps, ref: any): JSX.Element => {
    const [openNbd, setOpenNbd] = React.useState(false);
    const [popoverAnchor, setPopoverAnchor] = React.useState<Element | null>(null);
    const {
      handleSubmit,
      control,
      reset,
      formState: { isValid },
    } = useForm<any>({ mode: 'onChange' });

    const dispatch = useDispatch();

    const therapyFillCycleReasons = useSelector((state: IState) => {
      let t = ([] as (TherapyType | ITherapy)[]).concat(therapy)[0];
      if (!t || !t.id) {
        return state.lookups.therapyFillCycleReasons;
      }
      if (!('fill_cycle' in t)) {
        t = state.therapies.data[t.id];
      }
      return getTherapyFillCycleReasonOptions(state.lookups.therapyFillCycleReasons, t);
    });

    const currentTherapy = useSelector((state: IState) => {
      const currentTrp = Array.isArray(therapy) ? therapy[0] : therapy;
      if (!currentTrp?.id) {
        return null;
      }
      const trp = 'fill_cycle' in currentTrp ? currentTrp : state.therapies.data[currentTrp.id];
      return trp as ITherapy;
    });

    const initialNbdDate = React.useMemo(() => {
      if (props.initialDate && moment.isMoment(props.initialDate)) {
        return props.initialDate;
      }
      if (currentTherapy?.needsby_date) {
        return moment.utc(currentTherapy.needsby_date);
      }
      return null;
    }, [props.initialDate, currentTherapy?.needsby_date]);

    const handleOpenNbd = (e: any) => {
      setPopoverAnchor(e.currentTarget);
      setOpenNbd(true);
      reset({ [getFieldName('needsby_date')]: initialNbdDate });
    };

    const onSubmit: SubmitHandler<IFormFields> = data => {
      if (therapy) {
        const therapies = Array.isArray(therapy) ? therapy : [therapy];
        const updatedTherapy = therapies.map((item: any) => {
          const payload = {
            patient_id: item.patient_id,
            fill_cycle_reason_id: data.reason,
            therapy_id: item.id,
            needsby_date: convertToArborDate(data.needsby_date).getUtcDate(),
          };

          return payload;
        });
        dispatch(
          editTherapiesNBD(
            updatedTherapy,
            () => setOpenNbd(false),
            () => setOpenNbd(false),
          ),
        );
      }

      dispatch(clearSelectedTasks());
    };
    return (
      <>
        {iconButtonType ? (
          <IconButton onClick={handleOpenNbd} disabled={props.disabled} size="small">
            <CalendarToday fontSize="small" />
          </IconButton>
        ) : (
          <Button
            onClick={handleOpenNbd}
            variant="contained"
            color="primary"
            startIcon={<CalendarToday />}
            disabled={props.disabled}
          >
            Edit NBD
          </Button>
        )}
        <Popover
          ref={ref}
          id="followup-popover"
          open={openNbd}
          anchorEl={popoverAnchor}
          onClose={() => setOpenNbd(false)}
          onClick={e => {
            e.stopPropagation();
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box p={2} minWidth={336} display="flex" flexDirection="column" alignItems="center">
            <h3>Select Needs by Date</h3>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ControlledDatePickerCalendar
                defaultValue={initialNbdDate}
                name={getFieldName('needsby_date')}
                control={control}
              />
              <ControlledDropdown
                label="Reason for Change*"
                control={control}
                name={getFieldName('reason')}
                options={therapyFillCycleReasons}
                defaultValue={null}
                validations={{ required: true }}
                {...qaIdProp('reason')}
              />
              <ConfirmationPanel
                cancelButtonName="edit_therapy_cancel_button"
                submitButtonName="edit_therapy_submit_button"
                cancelButtonText="Cancel"
                submitButtonText="Save"
                disableSubmit={!isValid}
                handleCancel={() => setOpenNbd(false)}
              />
            </form>
          </Box>
        </Popover>
      </>
    );
  },
);
